import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import { FileText } from 'lucide-react';
import { ProjectScore } from '../../../types/judging.types';
import { Rubric } from '../../../types/rubric.types';
import { getRubricById } from '../../../services/rubric.service';

interface RubricScoreDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  score: ProjectScore & {
    rubricId?: string;
    rubricScores?: { questionId: string; pointsAwarded: number }[];
  };
}

const RubricScoreDetailsDialog: React.FC<RubricScoreDetailsDialogProps> = ({
  open,
  onClose,
  score,
}) => {
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Debug: Log the score data to see what we're working with
    console.log('RubricScoreDetailsDialog - Score data:', score);
    console.log('RubricId:', score.rubricId);
    console.log('RubricScores:', score.rubricScores);

    if (!open || !score.rubricId) {
      setRubric(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getRubricById(score.rubricId)
      .then((rubricData) => {
        console.log('Fetched rubric:', rubricData);
        setRubric(rubricData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching rubric:', err);
        setError('Failed to load rubric details');
        setLoading(false);
      });
  }, [open, score]);

  // Map rubricScores to questions
  const getQuestionScore = (questionId: string) => {
    const found = score.rubricScores?.find((s) => s.questionId === questionId);
    console.log(`Question ${questionId} score:`, found);
    return found;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <FileText size={20} />
          <Box>
            <Typography variant="h6">Rubric Score Details</Typography>
            <Typography variant="caption" color="text.secondary">
              Judge: {score.judgeName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !rubric ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography variant="body1" color="text.secondary">
              No rubric details available for this score.
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Rubric Header */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.lighter' }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="h6">{rubric.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Points: {rubric.totalPoints}
                  </Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h5" color="primary">
                    {score.score} / {rubric.totalPoints}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Score
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Debug Info (only if rubricScores is missing) */}
            {!score.rubricScores && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Warning: Individual question scores not found. Showing rubric
                structure only.
              </Alert>
            )}

            {/* Question Breakdown */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                Question Breakdown
              </Typography>
              {rubric.questions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No questions found in this rubric.
                </Typography>
              ) : (
                rubric.questions.map((question, index) => {
                  const questionScore = getQuestionScore(question.id);
                  const pointsAwarded = questionScore?.pointsAwarded ?? 0;

                  console.log(`Rendering question ${index + 1}:`, {
                    questionId: question.id,
                    questionText: question.question,
                    pointsAwarded,
                    maxPoints: question.maxPoints,
                    allRubricScores: score.rubricScores,
                  });

                  return (
                    <Paper
                      key={question.id}
                      sx={{ p: 2, mb: 2 }}
                      variant="outlined"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="start"
                      >
                        <Box flex={1}>
                          <Typography variant="subtitle2" gutterBottom>
                            Question {index + 1}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {question.question}
                          </Typography>
                          <Chip
                            label={`Max: ${question.maxPoints} points`}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        </Box>
                        <Box textAlign="right" ml={2}>
                          <Typography
                            variant="h6"
                            color={
                              pointsAwarded === question.maxPoints
                                ? 'success.main'
                                : pointsAwarded > 0
                                  ? 'warning.main'
                                  : 'text.secondary'
                            }
                          >
                            {pointsAwarded} / {question.maxPoints}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Points Awarded
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })
              )}
            </Box>

            {/* Summary */}
            <Divider sx={{ my: 3 }} />
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  Total Score
                </Typography>
                <Typography variant="h6" color="primary">
                  {score.score} / {rubric.totalPoints} (
                  {((score.score / rubric.totalPoints) * 100).toFixed(1)}%)
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RubricScoreDetailsDialog;
